import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Check } from 'lucide-react';
import type { AttuneSlideData, Slide, SlideData, Theme } from '../slideTypes';
import { streamAttuneChat, type AttuneMessage } from '../../../../lib/attune/streamClient';
import type { AttuneChatData } from '../../../../lib/attune/prompts';
import { startExperience, prebuildExperience, adoptExperience, extractDiagnostic, generateWrapup, stripInlineMarkdown, stripReadyMarker, trimStreamingMarker, isThinTranscript, READY_MARKER, type ExtractedDiagnostic, type AttuneWrapup, type PrebuiltExperience } from '../../../../lib/attune/experience';
import { AttuneWrapupPanel, type WrapupRoute } from './AttuneWrapup';
import { buildAttuneCourseContext, type AttuneCourseContext } from '../../../../lib/attune/courseContext';
import { attunePalette, attuneEmberPalette, ATTUNE_EMBER_THEME } from '../../../../lib/attune/palette';
import { attuneChipContext } from '../../../../lib/lessonContext';
import './attune.css';
import { recordFacilitation } from '../../../../services/gydFacilitationService';
import { recordDiagnostic, getPriorAttuneMemory } from '../../../../services/attuneDiagnosticsService';
import { MODALITIES, type ModalityKey } from '../../../../lib/scenario-builder/modality';
import { useOptionalAuth } from '../../../../context/AuthContext';
import { useOptionalTenant } from '../../../../context/TenantContext';

// Lazy import severs the AttuneSlide ⇄ SlideCanvas static cycle (SlideCanvas
// imports AttuneSlide; the Experience player renders generated slides back
// through SlideCanvas). The dynamic import resolves at play time, after both
// modules are initialised.
const SlideCanvasLazy = lazy(() => import('../SlideCanvas').then((m) => ({ default: m.SlideCanvas })));

// Generated arc slides that have no interaction to complete — the learner can
// advance immediately. Everything else (interactive types, reflection) gates the
// Next button until it signals completion.
const AUTO_ADVANCE_TEMPLATES = new Set(['text', 'cover', 'image', 'video', 'code', 'canva']);

// Hand-off frames for the send-off's close, so a learner who meets several
// Attune moments doesn't get the identical line each time — and to drop the
// "going in" facilitator-speak Dylan flagged. Chosen deterministically per
// slide (stable across re-renders, varies across lessons).
const HANDOFF_FRAMES: ((q: string) => string)[] = [
  (q) => `“${q}” — that's worth carrying with you.`,
  (q) => `You said it yourself: “${q}”.`,
  (q) => `“${q}” — keep that one close.`,
  (q) => `Worth holding onto: “${q}”.`,
];

// A reflection only earns the quote-back close when there is something in it
// to quote. Framing a hedge as a keepsake (“not sure” — keep that one close.)
// is the close faking a synthesis the learner never reached — the exact
// failure the facilitator spec forbids. Thin or non-committal reflections land
// on the honest neutral close instead.
const HEDGE_REFLECTIONS = new Set([
  'not sure', 'unsure', 'no', 'nope', 'yes', 'ok', 'okay', 'fine', 'maybe',
  'idk', 'dunno', 'i dont know', 'i do not know', 'im not sure', 'i am not sure',
  'not really', 'nothing', 'no idea', 'na', 'n a', 'none', 'skip', 'pass',
]);
export function isSubstantialReflection(s: string): boolean {
  const norm = s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
  if (norm.length < 20) return false;
  if (norm.split(' ').length < 4) return false;
  return !HEDGE_REFLECTIONS.has(norm);
}

// The native Attune slide — one moment, two roles: FACILITATOR (a reflective
// pause) and the end-of-lesson SEND-OFF (internal id 'experience' — a short
// diagnostic that then builds a personalised interactive). The old start-of-
// lesson "hook" role was removed 8 July 2026 (its craft + prompts preserved in
// Attune-Hook-Knowledge.md for the future Kula Create hook feature); the
// generation-layer hook support in lib/attune/experience.ts is intentionally
// kept for that reuse.

type AttuneMode = 'facilitator' | 'experience';

interface Recipe {
  /** The static first line, used when no generated opener is available. */
  opening: string;
  eyebrow: string;
}

// The conversation's PROMPTS (the diagnostic core, the facilitator operating
// spec, the closing turn, the course block) live on the server —
// lib/attune/prompts.ts — and are selected by the mode the client names; this
// component sends only the learner-facing strings it renders itself and the
// DATA the prompts are composed from (lesson slice, course placement, memory,
// the learner's name). See ATTUNE_CONVERSATION_SOURCES.md for the sources.
function getRecipe(mode: AttuneMode): Recipe {
  switch (mode) {
    case 'experience':
      return {
        eyebrow: 'made for you',
        // The first spoken line must carry the send-off's framing on its own:
        // a learner who clicked through the intro card would otherwise read
        // this as another facilitation pause and skip it. Lesson's over →
        // I'm building you something → the question.
        opening:
          "That's the lesson done — nothing more to get through. Before you go, I'd like to make you something special, built just for you from this conversation. So, be honest — maybe it all made sense, maybe some of it didn't. Where are you with it?",
      };
    case 'facilitator':
    default:
      return {
        eyebrow: 'a moment to reflect',
        opening:
          "Quick pause — I'm not here to test you, just to help you think it through. What's one bit of this that's landed for you, or one that hasn't?",
      };
  }
}

// The slice of course context the prompts read: placement + prior sections.
// aheadTitles is never sent — both roles never see ahead, preserving
// discovery. (The removed hook was the only role that read it; that field is
// left on AttuneCourseContext for the future creator hook.)
function courseSlice(ctx: AttuneCourseContext | null): AttuneChatData['courseContext'] {
  return ctx ? { placement: ctx.placement, before: ctx.before } : null;
}

interface AttuneSlideProps {
  data: AttuneSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: SlideData) => void;
  slideId: string;
  lessonId?: string;
  courseId?: string;
  /** Lesson context slices + this slide's index, supplied by the viewer.
   *  previousSlide (when present) is the slide the learner just left — the
   *  facilitator's "through glass" presentation renders it frosted behind the
   *  conversation. */
  attuneContext?: { before: string; after: string; whole: string; momentIndex: number; autoMode?: AttuneMode; previousSlide?: Slide | null };
  /** Course-level context supplied by the BUILDER preview (which withholds
   *  courseId/lessonId to stay memory- and capture-free). Ignored when the ids
   *  are present — the live viewer builds its own. Without either, an ungrounded
   *  moment confabulates a course, which is worse than no preview at all. */
  courseContext?: AttuneCourseContext | null;
  onComplete?: () => void;
  /** Advance to the next lesson slide — powers the close hand-off ("Continue ›").
   *  Absent in builder preview, where the close shows without the button. */
  onAdvance?: () => void;
  /** Label for the close hand-off when advancing means FINISHING (this Attune
   *  slide is the lesson's last): "Finish lesson ›" / "Finish course ›". Unset
   *  mid-lesson — the close falls back to its continue wording. */
  advanceLabel?: string;
  /** True when the learner has already completed this Attune slide this session.
   *  On re-entry we show a short "revisit" card (Continue / Start again) instead
   *  of re-running the whole conversation + build (which cost money and, worse,
   *  could leave the learner on a half-built or blank screen). */
  alreadyCompleted?: boolean;
}

export function AttuneSlide({ data, theme, isEditing, onUpdate, slideId, lessonId, courseId, attuneContext, courseContext, onComplete, onAdvance, advanceLabel, alreadyCompleted }: AttuneSlideProps) {
  if (isEditing) return <AttuneEditor data={data} onUpdate={onUpdate} />;
  return (
    <AttuneConversation
      data={data}
      theme={theme}
      courseId={courseId}
      lessonId={lessonId}
      slideId={slideId}
      attuneContext={attuneContext}
      courseContext={courseContext}
      onComplete={onComplete}
      onAdvance={onAdvance}
      advanceLabel={advanceLabel}
      alreadyCompleted={alreadyCompleted}
    />
  );
}

// ─── Editor (builder config) ──────────────────────────────────────────────

function AttuneEditor({ data, onUpdate }: { data: AttuneSlideData; onUpdate: (d: SlideData) => void }) {
  const set = (patch: Partial<AttuneSlideData>) => onUpdate({ ...data, ...patch });
  return (
    <div className="mx-auto max-w-lg p-8">
      <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
        <Sparkles size={16} /> ATTUNE · PERSONALISED AI MOMENT
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Leave it on Auto — facilitator anywhere in the lesson, a personalised send-off on the last slide — or pin a role.
      </p>

      <label className="mt-6 block text-sm text-gray-600">Mode</label>
      <select
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        value={data.mode ?? ''}
        onChange={(e) => set({ mode: (e.target.value || undefined) as AttuneMode | undefined })}
      >
        <option value="">Auto (by position)</option>
        <option value="facilitator">Facilitator</option>
        <option value="experience">Send-off</option>
      </select>

      <label className="mt-4 block text-sm text-gray-600">
        Focus <span className="text-gray-400">(optional)</span>
      </label>
      <input
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        placeholder="Leave blank to follow the lesson"
        value={data.topic ?? ''}
        onChange={(e) => set({ topic: e.target.value })}
      />
      <p className="mt-1 text-xs text-gray-400">
        Attune already reads this lesson. Add a focus only to point it at a specific angle.
      </p>

      <label className="mt-4 block text-sm text-gray-600">Audience</label>
      <select
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        value={data.audience ?? 'adult'}
        onChange={(e) => set({ audience: e.target.value as 'adult' | 'high-school' })}
      >
        <option value="adult">Adult</option>
        <option value="high-school">High school</option>
      </select>
    </div>
  );
}

// ─── Viewer (the conversation engine spine) ───────────────────────────────

interface ChatTurn { role: 'user' | 'assistant'; content: string }

function AttuneConversation({
  data,
  theme,
  courseId,
  lessonId,
  slideId,
  attuneContext,
  courseContext,
  onComplete,
  onAdvance,
  advanceLabel,
  alreadyCompleted,
}: {
  data: AttuneSlideData;
  theme: Theme;
  courseId?: string;
  lessonId?: string;
  slideId: string;
  attuneContext?: { before: string; after: string; whole: string; momentIndex: number; autoMode?: AttuneMode; previousSlide?: Slide | null };
  courseContext?: AttuneCourseContext | null;
  onComplete?: () => void;
  onAdvance?: () => void;
  advanceLabel?: string;
  alreadyCompleted?: boolean;
}) {
  // Explicit mode wins; otherwise resolve by position (send-off on the last
  // slide, facilitator everywhere else). The viewer/builder compute autoMode
  // from the lesson structure. Default facilitator when there's no position
  // signal at all (e.g. a bare preview).
  const mode: AttuneMode = data.mode ?? attuneContext?.autoMode ?? 'facilitator';
  const recipe = getRecipe(mode);
  // "Through glass": the facilitator's presentation keeps the learner visibly
  // inside the lesson — the slide they just left stays frosted behind the
  // conversation. The send-off wears its own aurora surface instead.
  const glass = mode === 'facilitator';
  // "One beat": the conversation grammar (one exchange on screen, adaptive
  // voice scale, replies condensing into revisitable dots, markdown-stripped
  // voice). The facilitator wears it over the frost; the end-experience tune
  // wears it over its own aurora surface — its old presentation rendered long
  // replies at display size and flung learner bubbles to the far edge.
  const oneBeat = glass || mode === 'experience';

  // The learner's first name, so the coach can greet/affirm them by name — used
  // sparingly; the server weaves it into the prompts. Optional auth so the dev
  // preview harness (no AuthProvider) doesn't throw.
  const auth = useOptionalAuth();
  // Optional for the same reason: outside a TenantProvider (preview harness)
  // there is no feature resolution, so the profile-narrative weave stays off.
  const tenantCtx = useOptionalTenant();
  const memoryOn = tenantCtx?.features.memory ?? false;
  const learnerName = (auth?.user?.name || '').trim().split(/\s+/)[0] || '';

  // Lets the end-experience "questions first" route reopen the conversation for a
  // few more turns before the wrap-up fires again.
  const [capBonus, setCapBonus] = useState(0);
  // How long the diagnostic runs before it draws to a natural close. Facilitator
  // and the send-off both run to a CONDITION rather than a count — the model owns
  // the stop decision (facilitator: one genuine beat of the learner's own
  // thinking, spec §I.7; send-off: the READY_MARKER when it has context + gap +
  // how-they-learn) — so these are only safety CEILINGS. The send-off gets the
  // longest ear (8): it builds a whole personalised piece from what it hears, and
  // a slow-opening learner needs the room. The learner can always end early
  // ("I'm done ›" / "I'm ready ›").
  const TURN_CAP = (mode === 'facilitator' ? 6 : 8) + capBonus;
  // The model may not close the experience diagnostic before this many learner
  // turns, however confident it feels — a premature READY is ignored.
  const READY_FLOOR = 3;
  // When we hit the ceiling, the assistant lands the conversation instead of
  // asking again (the server's wrap instruction, sent as `wrap: true`).

  // What this moment is, so the learner isn't dropped cold into a question.
  // The sub-line under the title — must NOT restate the title (it sits directly
  // beneath introTitle on the intro card). Explains, doesn't echo.
  const orientation =
    mode === 'experience'
      ? "The lesson itself is finished — this isn't more content or another reflection. A quick conversation about where you're at, then something is built just for you. Everyone did the same lesson; this part, only you get."
      : 'No test, no right answers — a few questions to help your own thinking land.';

  // Progressive context: facilitator reads what's BEFORE it; the send-off reads
  // the WHOLE lesson. The server prepends it so the moment is aware of the
  // lesson (and the facilitator only of what the learner has already seen).
  const contextSlice =
    mode === 'experience' ? attuneContext?.whole : attuneContext?.before;

  // Course-scoped memory of earlier Attune moments, so this feels like one
  // assistant who's been with the learner — fetched on mount, folded into the
  // prompt data + a personalised opener. Only attempted with a real
  // enrollment (courseId + lessonId); in Preview it stays the static opener.
  const hasCtx = Boolean(courseId && lessonId);
  const [memory, setMemory] = useState<string | null>(null);
  const [courseCtx, setCourseCtx] = useState<AttuneCourseContext | null>(null);

  // Builder preview: no ids (so no memory/capture), but the editor supplies
  // course context directly — the moment stays grounded in the real course
  // instead of confabulating one. Live viewer (ids present) fetches its own
  // in the boot effect below; the prop is ignored there.
  useEffect(() => {
    if (!hasCtx && courseContext) setCourseCtx(courseContext);
  }, [hasCtx, courseContext]);
  // Everything the server composes a turn's prompt from. The prompt text
  // itself (recipe, style rule, memory weave, course block) is server-owned.
  const chatData = useMemo<AttuneChatData>(
    () => ({
      role: mode,
      audience: data.audience,
      contextSlice,
      courseContext: courseSlice(courseCtx),
      memory,
      learnerName,
    }),
    [mode, data.audience, contextSlice, courseCtx, memory, learnerName],
  );

  // Re-entry: if the learner already finished this moment, show a short revisit
  // card instead of re-running the conversation + build. "Start again" clears it.
  const [revisit, setRevisit] = useState(Boolean(alreadyCompleted));
  const [turns, setTurns] = useState<ChatTurn[]>(
    hasCtx || alreadyCompleted ? [] : [{ role: 'assistant', content: recipe.opening }],
  );
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState('');
  const [busy, setBusy] = useState(hasCtx && !alreadyCompleted); // boot greeting in flight
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Experience close: the bridge reply ("I've got enough… let me play back what
  // I heard") stays on screen until the learner clicks "Show me ›" — the
  // hand-off to the wrap-up is learner-paced, never a timer.
  const [awaitReveal, setAwaitReveal] = useState(false);
  // A short intro card so a learner expecting a static slide knows this is an
  // interactive AI moment before it asks them anything. The glass facilitator
  // skips it: the frost arriving over the slide IS the threshold, and a
  // transient chip carries the orientation instead of a separate card.
  const [intro, setIntro] = useState(!glass);
  // The learner's final written reflection (from the generated arc), so the close
  // can acknowledge it instead of dropping it into a generic "thanks".
  const [finalReflection, setFinalReflection] = useState('');
  // Facilitator close state: the synthesis text (empty when the learner bailed
  // via End session, so the close never shows a dangling question), the opt-in
  // transcript, and which constellation dot is open for revisiting.
  const [wrapText, setWrapText] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  const [peekIdx, setPeekIdx] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Set below the transition handlers; lets `send` dispatch the natural close
  // (build / reflect / wrap) by mode without callback-ordering headaches.
  const flowRef = useRef<((convo: ChatTurn[]) => void) | null>(null);

  // Boot: load memory, then open with a personalised "I remember you" greeting
  // when there's history (else fall straight to the static opener). Best-effort
  // throughout — any failure lands on the static opener.
  useEffect(() => {
    if (!courseId || !lessonId) return; // preview: static opener already shown
    if (revisit) return; // showing the revisit card — don't boot a fresh moment
    let cancelled = false;
    void (async () => {
      let mem: string | null = null;
      let course: AttuneCourseContext | null = null;
      try {
        [mem, course] = await Promise.all([
          getPriorAttuneMemory({ courseId, slideId, includeProfile: memoryOn }),
          buildAttuneCourseContext(courseId, lessonId),
        ]);
      } catch { /* context is best-effort */ }
      if (cancelled) return;
      setCourseCtx(course);
      if (!mem) {
        // First contact. The facilitator opens with a GENERATED line that starts
        // inside this lesson's material and lightly frames "facilitator, not tutor"
        // (spec §I.7 wide-but-grounded + §I.14 first-contact framing). The send-off
        // keeps its static opener.
        if (mode === 'facilitator' && contextSlice) {
          let acc = '';
          try {
            await streamAttuneChat({
              mode: 'chat_first_contact',
              data: { role: mode, audience: data.audience, contextSlice, courseContext: courseSlice(course), learnerName },
              onText: (delta) => { if (!cancelled) { acc += delta; setStreaming(acc); } },
            });
          } catch { /* fall back to the static opener */ }
          if (cancelled) return;
          setStreaming('');
          setTurns([{ role: 'assistant', content: acc.trim() || recipe.opening }]);
          setBusy(false);
          return;
        }
        setTurns([{ role: 'assistant', content: recipe.opening }]);
        setBusy(false);
        return;
      }
      setMemory(mem);
      // A personalised "I remember you" greeting (the send-off's version also
      // carries the lesson-is-over framing — server-side, keyed on the role).
      let acc = '';
      try {
        await streamAttuneChat({
          mode: 'chat_greet',
          data: { role: mode, audience: data.audience, contextSlice, courseContext: courseSlice(course), memory: mem, learnerName },
          onText: (delta) => { if (!cancelled) { acc += delta; setStreaming(acc); } },
        });
      } catch { /* fall back to the static opener */ }
      if (cancelled) return;
      setStreaming('');
      setTurns([{ role: 'assistant', content: acc.trim() || recipe.opening }]);
      setBusy(false);
    })();
    return () => { cancelled = true; };
    // Re-runs when `revisit` flips false ("Start again"); otherwise boots once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisit]);

  // Start the moment over from a revisit — reset the conversation/build state and
  // let the boot effect (dep: revisit) re-run.
  const startAgain = useCallback(() => {
    setTurns([]);
    setStreaming('');
    setError(null);
    setDone(false);
    setIntro(!glass);
    setWrapText('');
    setShowTranscript(false);
    setBusy(hasCtx);
    setRevisit(false);
  }, [hasCtx, glass]);

  // Best-effort capture — facilitator only for now; no-op without an enrollment
  // (e.g. instructor Preview) and behind the fk_attune_capture kill-switch.
  const capture = useCallback(
    (allTurns: ChatTurn[], closingText?: string) => {
      if (!courseId || !lessonId) return;
      if (mode === 'facilitator') {
        const userTurns = allTurns.filter((t) => t.role === 'user').length;
        void recordFacilitation({
          courseId,
          lessonId,
          slideId,
          touchpointPosition: attuneContext?.momentIndex ?? null,
          transcript: allTurns,
          wrapupInsight: closingText ?? null,
          turnCount: userTurns,
        }).catch(() => { /* best-effort */ });
      } else {
        // send-off → attune_diagnostics (the summary is the last turn).
        void recordDiagnostic({
          courseId,
          lessonId,
          slideId,
          mode,
          transcript: allTurns,
        }).catch(() => { /* best-effort */ });
      }
    },
    [mode, courseId, lessonId, slideId, attuneContext],
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setError(null);
    const next: ChatTurn[] = [...turns, { role: 'user', content: text }];
    setTurns(next);
    setBusy(true);
    setStreaming('');
    const userTurns = next.filter((t) => t.role === 'user').length;
    // The safety ceiling: past it the assistant lands the conversation instead
    // of asking again. (The experience's REAL stop is the model's READY marker
    // below — the ceiling only catches a conversation that never converges.)
    const atCap = userTurns >= TURN_CAP;
    // Facilitator closes with a SINGLE synthesis (finish) — skip the normal at-cap
    // reply so the learner doesn't get a wrap sentence and then a separate close.
    if (atCap && mode === 'facilitator') {
      flowRef.current?.(next);
      return;
    }
    let after = next;
    let closeNow = atCap;
    try {
      const messages: AttuneMessage[] = next.map((t) => ({ role: t.role, content: t.content }));
      let acc = '';
      await streamAttuneChat({
        mode: 'chat_turn',
        data: { ...chatData, wrap: atCap, messages },
        // Hide any trailing partial READY marker mid-stream so "[REA" never flashes.
        onText: (delta) => { acc += delta; setStreaming(mode === 'experience' ? trimStreamingMarker(acc) : acc); },
      });
      let replyText = acc;
      if (mode === 'experience') {
        // The model owns the stop decision: a READY-marked reply IS the bridge
        // into the wrap-up ("I've got enough… let me play back what I heard").
        // Premature confidence below the floor is ignored — marker stripped,
        // conversation continues — so the build never rests on pleasantries.
        const { text: stripped, ready } = stripReadyMarker(acc);
        replyText = stripped;
        if (ready && userTurns >= READY_FLOOR) closeNow = true;
      }
      after = [...next, { role: 'assistant' as const, content: replyText }];
      setTurns(after);
      capture(after);
    } catch (e) {
      setError((e as Error).message || 'Something went wrong.');
      setStreaming('');
      setBusy(false);
      return;
    }
    setStreaming('');
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }));
    if (closeNow) {
      // Only the send-off reaches here (facilitator closes via finish at the cap
      // check above). The bridge stays on screen until the learner asks for the
      // playback — learner-paced, not a timer.
      setBusy(false);
      setAwaitReveal(true);
      return;
    }
    setBusy(false);
  }, [input, busy, turns, chatData, capture, mode, TURN_CAP, READY_FLOOR]);

  // Facilitator close: a brief reflective wrap-up (text), then done. (The
  // send-off builds an interactive instead — see beginWrapup / handleRoute.)
  const finish = useCallback(async (convo: ChatTurn[] = turns) => {
    setBusy(true);
    let wrapup = '';
    try {
      // The server ends the message list on a USER turn ("close us out") so the
      // model writes a FRESH closing turn rather than prefilling the assistant's
      // last question — that's how the close used to come back empty and fall
      // through to a dangling question on screen.
      const messages: AttuneMessage[] = convo.map((t) => ({ role: t.role, content: t.content }));
      await streamAttuneChat({
        mode: 'chat_close',
        data: { role: mode, audience: data.audience, learnerName, messages },
        onText: (delta) => { wrapup += delta; setStreaming(wrapup); },
      });
    } catch {
      /* wrap-up is best-effort */
    }
    setStreaming('');
    // Always close on a non-question wrap-up — a warm fallback if generation came
    // back empty — so the session never ends on a dangling question.
    const wrap = wrapup.trim() || 'Thanks for thinking that through — carry it with you.';
    const finalTurns = [...convo, { role: 'assistant' as const, content: wrap }];
    setTurns(finalTurns);
    setWrapText(wrap);
    capture(finalTurns, wrap);
    setBusy(false);
    setDone(true);
    onComplete?.();
  }, [turns, capture, onComplete, mode, data.audience, learnerName]);

  // "I'm ready ›" during the experience diagnostic. This used to jump straight
  // to the wrap-up, cutting the conversation mid-question — now it gets the same
  // bridge beat as the natural close. Canned rather than generated: the learner
  // just opted out of talking, so the response should be instant.
  const handleReadyEarly = useCallback(() => {
    if (mode !== 'experience') {
      flowRef.current?.(turns);
      return;
    }
    const bridge = "Good — I've got enough to build you something. Let me play back what I heard.";
    const after: ChatTurn[] = [...turns, { role: 'assistant', content: bridge }];
    setTurns(after);
    capture(after);
    setAwaitReveal(true);
  }, [mode, turns, capture]);

  // Experience mode: the conversation hard-stops into the reflective wrap-up (the
  // learner's thinking mirrored back), they pick a route, and we BUILD a
  // personalised mini-lesson played inline on the course theme. The framing
  // (Haiku, fast) is revealed the moment it's ready while the interactive (Sonnet,
  // slow) builds behind it — so the learner reads instead of staring at a spinner.
  // See lib/attune/experience.ts.
  type ExpPhase = 'chat' | 'wrapup' | 'building' | 'playing';
  const [phase, setPhase] = useState<ExpPhase>('chat');
  const [diagnostic, setDiagnostic] = useState<ExtractedDiagnostic | null>(null);
  const [wrapup, setWrapup] = useState<AttuneWrapup | null>(null);
  const [thinSignal, setThinSignal] = useState(false);
  const [frameSlide, setFrameSlide] = useState<Slide | null>(null);
  const [restSlides, setRestSlides] = useState<Slide[]>([]);
  const [restPending, setRestPending] = useState(false);
  const [genIdx, setGenIdx] = useState(0);
  const [genCompleted, setGenCompleted] = useState(false);
  const [advancePending, setAdvancePending] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [chosenLabel, setChosenLabel] = useState('');

  // Speculative build: fired the moment the wrap-up renders, so the Sonnet
  // interactive builds while the learner reads. Both build routes produce the
  // same scenario from the same diagnostic, so the work is route-independent;
  // launchBuild adopts it, "more questions" aborts it. convoLen guards against
  // adopting a build whose transcript is stale.
  const speculativeRef = useRef<{ pre: PrebuiltExperience; convoLen: number } | null>(null);
  useEffect(() => () => { speculativeRef.current?.pre.abort(); }, []);

  const genSlides = useMemo<Slide[]>(
    () => [...(frameSlide ? [frameSlide] : []), ...restSlides],
    [frameSlide, restSlides],
  );

  // Staged status — make the build feel purposeful instead of a dead spinner.
  const statusMessages = useMemo(
    () => [
      'Reading what you told me…',
      `Designing your ${chosenLabel || 'experience'}…`,
      'Writing the details…',
      'Almost there…',
    ],
    [chosenLabel],
  );
  const [statusIdx, setStatusIdx] = useState(0);
  useEffect(() => {
    const buildingNow = phase === 'building' || advancePending;
    if (!buildingNow) {
      setStatusIdx(0);
      return;
    }
    const t = setInterval(() => setStatusIdx((i) => Math.min(i + 1, statusMessages.length - 1)), 4500);
    return () => clearInterval(t);
  }, [phase, advancePending, statusMessages.length]);
  const currentStatus = advancePending ? 'Bringing your experience together…' : statusMessages[statusIdx];

  const finishExperience = useCallback(() => {
    setDone(true);
    onComplete?.();
  }, [onComplete]);

  // Experience: hard stop on the conversation → synthesise the reflective wrap-up
  // (one cheap Haiku call) → the learner reads their own thinking mirrored back and
  // picks a route into the build, all in one flow. generateWrapup never throws —
  // it falls back to a diagnostic-derived wrap-up so this always lands.
  const beginWrapup = useCallback(async (convo: ChatTurn[] = turns) => {
    setPhase('wrapup');
    setWrapup(null);
    // A conversation of one-word answers can't carry a quote panel or a
    // trip-up insight — the wrap-up degrades honestly rather than performing
    // certainty it doesn't have.
    setThinSignal(isThinTranscript(convo));
    setBusy(true);
    let d: ExtractedDiagnostic;
    try {
      d = await extractDiagnostic({
        transcript: convo,
        audience: data.audience,
        lessonContext: attuneContext?.whole,
      });
    } catch {
      d = { learningFocus: '', realContext: '', keyGap: '', suggestedModality: 'scenario', motivationHook: '' };
    }
    setDiagnostic(d);
    const w = await generateWrapup({
      transcript: convo,
      diagnostic: d,
      audience: data.audience,
      lessonContext: attuneContext?.whole,
      learnerName,
    });
    setWrapup(w);
    setBusy(false);
    // The learner now reads the wrap-up (typically 15–40s) before picking a
    // route — start the build NOW so it's largely done by the time they click.
    // Input must match launchBuild's exactly (same strength construction) or
    // the adopted build wouldn't be the one the route asked for.
    speculativeRef.current?.pre.abort();
    speculativeRef.current = {
      pre: prebuildExperience({
        modality: 'scenario',
        transcript: convo,
        topic: data.topic,
        audience: data.audience,
        position: 'experience',
        lessonContext: attuneContext?.whole,
        courseContext: courseCtx?.before || undefined,
        diagnostic: d,
        strength: `${w.strengthTitle} — ${w.strengthBody}`,
      }),
      convoLen: convo.length,
    };
    if (courseId && lessonId) {
      void recordDiagnostic({
        courseId, lessonId, slideId, mode: 'experience',
        transcript: convo, diagnostic: d, reflection: w.quoteReading || undefined,
      }).catch(() => { /* best-effort */ });
    }
  }, [turns, data.topic, data.audience, attuneContext, courseCtx, learnerName, courseId, lessonId, slideId]);

  // The send-off's build launcher. Frame-first: reveal the framing the moment
  // it's ready while the interactive builds behind it. Reads the WHOLE lesson.
  // (startExperience still accepts position: 'hook' — kept for the future
  // creator hook — but Attune only ever builds from the end now.)
  const launchBuild = useCallback(
    (key: ModalityKey, convo: ChatTurn[], diag: ExtractedDiagnostic | null, light = false, strength?: string) => {
      setBuildError(null);
      setFrameSlide(null);
      setRestSlides([]);
      setGenIdx(0);
      setGenCompleted(false);
      setAdvancePending(false);
      setRestPending(true);
      setChosenLabel(MODALITIES[key]?.label ?? '');
      setPhase('building');

      // Adopt the speculative build fired at wrap-up render when it matches
      // this launch (same transcript, scenario route) — the interactive is
      // then already minutes-of-seconds ahead. Consume it either way so a
      // failed speculation is never re-adopted.
      const spec = speculativeRef.current;
      speculativeRef.current = null;
      if (spec && (key !== 'scenario' || spec.convoLen !== convo.length)) {
        spec.pre.abort(); // stale — cancel its in-flight calls
      }
      const build = spec && key === 'scenario' && spec.convoLen === convo.length
        ? adoptExperience(spec.pre, !light)
        : startExperience({
            modality: key,
            transcript: convo,
            topic: data.topic,
            audience: data.audience,
            position: 'experience',
            lessonContext: attuneContext?.whole,
            courseContext: courseCtx?.before || undefined,
            diagnostic: diag ?? undefined,
            strength, // "both doors" — stretch a strength instead of drilling a gap
            includeArc: !light, // the "quick" route builds the interactive only
          });

      build.frame
        .then((f) => { if (f) { setFrameSlide(f); setPhase('playing'); } })
        .catch(() => { /* frame is best-effort */ });

      build.rest
        .then(({ slides }) => {
          if (!slides.length) throw new Error('No experience was produced.');
          setRestSlides(slides);
          setRestPending(false);
          setPhase('playing');
        })
        .catch((e) => {
          setBuildError((e as Error).message || 'Could not build your experience.');
          setRestPending(false);
        });
    },
    [data.topic, data.audience, attuneContext, courseCtx],
  );

  // Experience: the learner picked a route from the wrap-up. "questions" reopens
  // the conversation for a few more turns; "method" is the genuinely short build
  // (interactive only, no surrounding arc); "scenario" is the full walk-through.
  const handleRoute = useCallback(
    (route: WrapupRoute) => {
      if (route === 'questions') {
        // No build coming from this transcript — stop the speculative calls.
        speculativeRef.current?.pre.abort();
        speculativeRef.current = null;
        setCapBonus((c) => c + 3);
        setPhase('chat');
        setBusy(false);
        return;
      }
      // End-of-lesson Attune builds a scenario, deliberately: it's the type
      // where the learner's own words become the scene, and one type means
      // the speculative build above is always the right one. ('apply' and
      // 'scenario' both resolved to scenario-simulation anyway — this makes
      // that the design rather than a coincidence of cluster ranking.)
      const key: ModalityKey = 'scenario';
      if (courseId && lessonId) {
        void recordDiagnostic({
          courseId, lessonId, slideId, mode: 'experience',
          transcript: turns, diagnostic: diagnostic ?? undefined,
          reflection: wrapup?.quoteReading || undefined, chosenModality: key,
        }).catch(() => { /* best-effort */ });
      }
      const strength = wrapup ? `${wrapup.strengthTitle} — ${wrapup.strengthBody}` : undefined;
      launchBuild(key, turns, diagnostic, route === 'method', strength);
    },
    [courseId, lessonId, slideId, turns, diagnostic, wrapup, launchBuild],
  );

  // Dispatch the natural close by mode (called by send at the turn cap, and by the
  // chat footer button). Reassigned each render so it always sees the latest mode.
  flowRef.current = (convo: ChatTurn[]) => {
    if (mode === 'experience') void beginWrapup(convo);
    else void finish(convo);
  };

  // Non-interactive arc slides (the framing intro) never fire onSlideCompleted,
  // so let the learner advance straight past them; gate only the slides that do.
  const currentGenGates = !AUTO_ADVANCE_TEMPLATES.has(genSlides[genIdx]?.data?.template ?? '');
  const atEndOfLoaded = genIdx >= genSlides.length - 1;
  // Only truly the end once the interactive has landed — while it's still
  // building behind the frame, the button is "Next", not "Finish".
  const isTrulyLast = !restPending && atEndOfLoaded;

  const advance = useCallback(() => {
    if (genIdx < genSlides.length - 1) {
      setGenIdx((i) => i + 1);
      setGenCompleted(false);
    } else if (restPending) {
      setAdvancePending(true); // wait for the interactive, then auto-advance
    } else {
      finishExperience();
    }
  }, [genIdx, genSlides.length, restPending, finishExperience]);

  // Once the interactive lands while the learner was waiting, step into it.
  useEffect(() => {
    if (advancePending && genSlides.length > genIdx + 1) {
      setGenIdx((i) => i + 1);
      setGenCompleted(false);
      setAdvancePending(false);
    }
  }, [advancePending, genSlides.length, genIdx]);

  // The Atelier: from the wrap-up onwards the experience stays in the Ember
  // night room regardless of course light/dark — the built piece arrives in
  // the same room the playback ended in, visibly Attune's and nobody else's.
  const emberRoom = mode === 'experience' && phase !== 'chat';
  const palette = useMemo(
    () => (emberRoom ? attuneEmberPalette() : attunePalette(theme)),
    [theme, emberRoom],
  );
  const rootStyle = {
    ...palette.vars,
    // The conversation voice is Attune's own, not the course heading font —
    // Hanken Grotesk is already loaded globally by the app's font link.
    // --att-display (theme heading) remains for the wrap-up surface only.
    '--att-voice': '"Hanken Grotesk", "Optima", "Seravek", "Segoe UI", sans-serif',
    '--att-display': theme.fonts?.heading,
    '--att-mono': theme.fonts?.code,
  } as React.CSSProperties;

  // The send-off wears its promise from the first turn — the same tag the
  // wrap-up and playback phases already carry — so even the corner chrome says
  // this is not another reflection pause.
  const eyebrowWord = mode === 'experience' ? 'made for you' : 'reflect';
  const introTitle = mode === 'experience' ? "You've reached the end — this part is made for you" : 'A moment to think it through';
  const lastTurn = turns[turns.length - 1];
  // Close headline: facilitator shows its wrap-up; the send-off acknowledges the
  // learner's final written reflection (their own words) when there is one.
  const reflectionClip = finalReflection.trim().slice(0, 140) + (finalReflection.trim().length > 140 ? '…' : '');
  const handoffFrame =
    HANDOFF_FRAMES[slideId.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % HANDOFF_FRAMES.length];
  const closeHeadline =
    mode === 'facilitator'
      // wrapText is only set by finish(); an End-session bail lands on the warm
      // static line instead of the last assistant turn (a dangling question).
      ? (wrapText || 'Thanks for thinking that through — the lesson is right where you left it.')
      : isSubstantialReflection(finalReflection)
        ? handoffFrame(reflectionClip)
        // Context-free on purpose: this line also shows on a course's final
        // slide, where "take it into the lesson" reads wrong next to Finish.
        : "That's yours now — carry it with you.";
  const showThinking = busy && !streaming;
  const inquiryText = streaming || (lastTurn?.role === 'assistant' ? lastTurn.content : '');
  const historyTurns =
    !streaming && !busy && lastTurn?.role === 'assistant' ? turns.slice(0, -1) : turns;

  // ── Glass presentation derivations (facilitator only) ──────────────────────
  // Each learner reply paired with the question it answered — one dot each.
  const beats = useMemo(() => {
    const out: { q: string; a: string }[] = [];
    let lastQ = '';
    for (const t of turns) {
      if (t.role === 'assistant') lastQ = t.content;
      else out.push({ q: lastQ, a: t.content });
    }
    return out;
  }, [turns]);
  // The voice is rendered verbatim in display type, so guarantee no markdown
  // reaches the learner regardless of what the model emitted.
  const voiceText = oneBeat ? stripInlineMarkdown(inquiryText) : inquiryText;
  // Adaptive voice scale: a one-liner earns display size; a paragraph steps
  // down, so the current message plus the composer always fit without scrolling.
  const voiceClass = !oneBeat ? '' : voiceText.length > 220 ? ' attg-len-long' : voiceText.length > 90 ? ' attg-len-mid' : ' attg-len-short';
  // The frost only re-renders the previous slide when it is a side-effect-free
  // static template (no autoplaying video, no embeds, no interactives re-firing
  // handlers). Anything else gets the veil over the themed surface instead.
  const FROST_SAFE_TEMPLATES = ['text', 'cover', 'image', 'code'];
  const prevSlide = attuneContext?.previousSlide ?? null;
  const frostSlide =
    glass && prevSlide && FROST_SAFE_TEMPLATES.includes(prevSlide.data?.template ?? '') ? prevSlide : null;
  // When the real pixels can't be safely re-rendered (webapps reload, videos
  // replay, interactives re-fire), frost the slide's GHOST instead: its own
  // title in large blurred type behind the veil. The glass never goes flat —
  // it's over the slide when safe, over the slide's memory when not.
  const prevData = prevSlide?.data as { title?: string; heading?: string } | undefined;
  const ghostTitle =
    glass && prevSlide && !frostSlide ? (prevData?.title || prevData?.heading || '').trim() : '';
  const modeTag =
    phase === 'playing'
      ? !restPending && genSlides.length > 1
        ? `${genIdx + 1} / ${genSlides.length}`
        : 'made for you'
      : phase === 'building' || advancePending
        ? 'building'
        : phase === 'wrapup'
          ? 'made for you'
          : eyebrowWord;

  const sendArrow = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );

  return (
    <div className={glass ? 'att att-glass' : 'att'} style={rootStyle}>
      {glass ? (
        // Through glass: the slide the learner just left stays visible beneath
        // a frost, so the moment reads as a different interaction inside the
        // lesson, not a different place. Inert and decorative by design.
        <div className="attg-frost" aria-hidden="true">
          {frostSlide ? (
            <div className="attg-frost-slide">
              <Suspense fallback={null}>
                <SlideCanvasLazy
                  slide={frostSlide}
                  theme={theme}
                  isEditing={false}
                  onUpdate={() => {}}
                  scenarioSavedResponse={null}
                  onActivityResponse={() => {}}
                />
              </Suspense>
            </div>
          ) : ghostTitle ? (
            <div className="attg-ghost">
              <span>{ghostTitle}</span>
            </div>
          ) : null}
          <div className="attg-veil" />
        </div>
      ) : (
        <div className="att-aurora" />
      )}

      <div className="att-head">
        <span className="att-eyebrow"><span className="att-dot" /><span>attune</span></span>
        <span className="att-mode">{modeTag}</span>
      </div>

      {!oneBeat && !revisit && !intro && !done && phase === 'chat' && (
        <div className="att-orient">{orientation}</div>
      )}

      {revisit ? (
        <div className="att-intro">
          <span className="att-presence att-intro-presence" aria-hidden="true" />
          <span className="att-intro-eyebrow">a moment with attune</span>
          <h1 className="att-intro-title">You've been through this one</h1>
          <p className="att-intro-sub">You already did this moment. Carry on into the lesson, or start it over.</p>
          <div className="att-actionbar" style={{ justifyContent: 'center', gap: 18, marginTop: 24 }}>
            <button className="att-link" onClick={startAgain}>Start again</button>
            {onAdvance && (
              <button className="att-intro-go" onClick={onAdvance}>{advanceLabel ?? 'Continue to the lesson ›'}</button>
            )}
          </div>
        </div>
      ) : intro ? (
        <div className="att-intro">
          <span className="att-presence att-intro-presence" aria-hidden="true" />
          <span className="att-intro-eyebrow">a moment with attune</span>
          <h1 className="att-intro-title">{introTitle}</h1>
          <p className="att-intro-sub">{orientation}</p>
          <button className="att-intro-go" onClick={() => setIntro(false)}>Begin ›</button>
        </div>
      ) : done ? (
        glass ? (
          // The curated close: the spoken synthesis is the summary, one beat of
          // the learner's own words is kept, and the full conversation is there
          // behind a quiet link — opt-in, never replayed by default.
          <>
            <div className="attg-close">
              <div className="attg-close-body">
                <h1 className="att-inquiry attg-len-mid">{stripInlineMarkdown(closeHeadline)}</h1>
                {beats.length > 0 && (
                  <div className="attg-keep">
                    <span className="attg-beatdot attg-beatdot-in" aria-hidden="true" />
                    <div>
                      <p className="attg-keep-k">In your words</p>
                      <p className="attg-keep-a">“{beats[beats.length - 1].a}”</p>
                    </div>
                  </div>
                )}
                {beats.length > 0 && (
                  <button className="att-link attg-translink" onClick={() => setShowTranscript((v) => !v)}>
                    {showTranscript ? 'Hide the conversation' : 'See the whole conversation ›'}
                  </button>
                )}
                {showTranscript && (
                  <div className="attg-transcript">
                    {turns.map((t, i) => (
                      <div
                        key={i}
                        className={t.role === 'assistant' ? 'att-turn att-turn-attune' : 'att-turn att-turn-them att-turn-you'}
                      >
                        {t.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="att-foot">
              <div className="att-actionbar">
                <span className="att-done"><Check size={15} /> done</span>
                {onAdvance && (
                  <button className="att-link att-link-go" onClick={onAdvance}>{advanceLabel ?? 'On to the next slide ›'}</button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="att-current" style={{ marginTop: 'auto' }}>
              <h1 className="att-inquiry">{closeHeadline}</h1>
            </div>
            <div className="att-foot">
              <div className="att-actionbar">
                <span className="att-done"><Check size={15} /> done</span>
                {onAdvance && (
                  <button className="att-link att-link-go" onClick={onAdvance}>{advanceLabel ?? 'Continue to the lesson ›'}</button>
                )}
              </div>
            </div>
          </>
        )
      ) : buildError ? (
        <>
          <div className="att-building"><p className="att-status">{buildError}</p></div>
          <div className="att-foot">
            <div className="att-actionbar">
              <button className="att-link att-link-go" onClick={finishExperience}>Continue to the lesson ›</button>
            </div>
          </div>
        </>
      ) : phase === 'building' || advancePending ? (
        <>
          <div className="att-building"><div className="att-spinner" /><p className="att-status">{currentStatus}</p></div>
          <div className="att-foot">
            <div className="att-actionbar"><button className="att-link" onClick={finishExperience}>skip</button></div>
          </div>
        </>
      ) : phase === 'playing' && genSlides[genIdx] ? (
        <>
          {/* The piece carries the learner's fingerprints: made-for-you mark +
              how-they-learn tags from the diagnostic. Direction-independent
              magic — nothing says "built for you" like your own traits on it. */}
          <div className="att-made">
            <span className="att-made-eyebrow">Made for you · from our conversation</span>
            {(wrapup?.chips?.length ?? 0) > 0 && (
              <span className="att-made-tags">
                {wrapup!.chips.slice(0, 3).map((c, i) => (
                  <span key={i} className="att-made-tag">{c}</span>
                ))}
              </span>
            )}
          </div>
          <div className="att-player">
            <Suspense fallback={<div className="att-building"><div className="att-spinner" /></div>}>
              <SlideCanvasLazy
                slide={genSlides[genIdx]}
                theme={ATTUNE_EMBER_THEME}
                isEditing={false}
                onUpdate={() => {}}
                scenarioSavedResponse={null}
                onActivityResponse={() => {}}
                onReflectionSave={(_id, answers) => setFinalReflection(Object.values(answers).filter(Boolean).join(' '))}
                onSlideCompleted={() => setGenCompleted(true)}
              />
            </Suspense>
          </div>
          <div className="att-foot">
            <div className="att-actionbar">
              <button className="att-link" onClick={finishExperience}>skip</button>
              <button className="att-link att-link-go" onClick={advance} disabled={currentGenGates && !genCompleted}>
                {isTrulyLast ? 'Finish ›' : 'Next ›'}
              </button>
            </div>
          </div>
        </>
      ) : phase === 'wrapup' ? (
        !wrapup ? (
          <>
            <div className="att-building"><div className="att-spinner" /><p className="att-status">Making sense of what you said…</p></div>
            <div className="att-foot"><div className="att-actionbar"><button className="att-link" onClick={finishExperience}>skip</button></div></div>
          </>
        ) : (
          <AttuneWrapupPanel wrapup={wrapup} thin={thinSignal} onPick={handleRoute} />
        )
      ) : (
        <>
          {oneBeat ? (
            // One exchange on screen at a time. The thread never stacks up as
            // bubbles: each reply condenses into a dot the learner can revisit.
            // Facilitator: the frosted lesson shows through above the voice and
            // the chip orients. Experience: its own aurora surface, and the
            // intro card already did the orienting — no chip.
            <div className="attg-stage">
              {glass && (
                <div className={`attg-chip${beats.length > 0 ? ' attg-chip-gone' : ''}`}>
                  A moment with Attune · {attuneChipContext(prevSlide?.data?.template)}
                </div>
              )}
              <div className="attg-stagegap" />
              <div className="attg-constel">
                {/* The thread: past exchanges recede along a hairline into the
                    present moment — the breathing dot at its end IS the
                    presence, so the dots and the "alive" mark are one object. */}
                <div className="attg-thread">
                  {beats.map((b, i) => (
                    <React.Fragment key={i}>
                      <button
                        className="attg-beatdot attg-beatdot-in"
                        aria-label={`Revisit exchange ${i + 1}`}
                        onClick={() => setPeekIdx(i)}
                      />
                      <span className="attg-threadlink" aria-hidden="true" />
                    </React.Fragment>
                  ))}
                  <span className="att-presence" aria-hidden="true" />
                </div>
                {beats.length === 1 && (
                  <span className="attg-dotcap">Your words, kept as a light — tap to revisit</span>
                )}
              </div>
              <div className="att-current attg-current">
                <h1 className={`att-inquiry${voiceClass}`}>
                  {showThinking ? <span className="att-thinking"><i /><i /><i /></span> : voiceText}
                </h1>
                {error && <div className="att-error">{error}</div>}
              </div>
            </div>
          ) : (
            <>
              <div className="att-history" ref={scrollRef}>
                {historyTurns.map((t, i) => (
                  <div
                    key={i}
                    className={t.role === 'assistant' ? 'att-turn att-turn-attune' : 'att-turn att-turn-them att-turn-you'}
                  >
                    {t.content}
                  </div>
                ))}
              </div>
              <div className="att-current">
                <h1 className="att-inquiry">
                  {showThinking ? <span className="att-thinking"><i /><i /><i /></span> : inquiryText}
                </h1>
                {error && <div className="att-error">{error}</div>}
              </div>
            </>
          )}
          <div className="att-foot">
            {awaitReveal ? (
              // The conversation has landed; the bridge is on screen. One
              // affordance, learner-paced: the playback starts when they ask.
              <div className="att-composer">
                <button
                  className="att-intro-go"
                  onClick={() => { setAwaitReveal(false); flowRef.current?.(turns); }}
                >
                  Show me ›
                </button>
              </div>
            ) : (
              <>
                <div className="att-composer">
                  <label className="att-pill">
                    <input
                      placeholder="Share your perspective…"
                      value={input}
                      disabled={busy}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
                    />
                  </label>
                  <button className="att-send" onClick={() => void send()} disabled={busy || !input.trim()} aria-label="send">
                    {sendArrow}
                  </button>
                </div>
                <div className="att-actionbar">
                  <button className="att-link" onClick={finishExperience}>End session</button>
                  <button
                    className="att-link att-link-go"
                    disabled={busy}
                    onClick={handleReadyEarly}
                  >
                    {mode === 'facilitator' ? "I'm done ›" : "I'm ready ›"}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {oneBeat && peekIdx !== null && beats[peekIdx] && (
        // Revisit a condensed exchange. History must be effortless to reach
        // precisely because it is not on screen.
        <div className="attg-peek" onClick={() => setPeekIdx(null)}>
          <div className="attg-peek-card">
            <div className="attg-peek-k">Exchange {peekIdx + 1}</div>
            <p className="attg-peek-q">{stripInlineMarkdown(beats[peekIdx].q)}</p>
            <p className="attg-peek-a">“{beats[peekIdx].a}”</p>
            <div className="attg-peek-hint">Click anywhere to return</div>
          </div>
        </div>
      )}
    </div>
  );
}
